import { Controller } from "@hotwired/stimulus"
import {
  BATTERY_MONITOR_KEY,
  batteryMonitoringEnabled,
  getBatterySnapshot,
  initBatteryMonitor,
  stopBatteryMonitor
} from "../battery_monitor"
import { logEvent } from "../field_logger"
import { foregroundTimeStamp, logForegroundShiftEnd } from "../field_foreground_time"

export { BATTERY_MONITOR_KEY }

export const BATTERY_READING_INTERVAL_MS = 30 * 60 * 1000
export const BATTERY_READING_LAST_KEY = "field:battery_reading_last_at"
export const BATTERY_READING_DEV_INTERVAL_KEY = "field:battery_reading_interval_ms"

const CHECK_EVERY_MS = 15_000

// Page JS outlives Turbo visits, so this survives a navigation that replaces
// the body (and this controller) or storage being blocked.
let lastReadingAtFallback = null

// Battery monitoring for the field app (cards #1124, #4143).
//
// Mounted on the field layout <body>, so it runs on every page, not just the
// shift pages. On a device with the Battery Status API (Android Chrome) it:
//   - listens for charging changes and the low (15%) / critical (5%) warnings
//   - logs a "Battery reading" every 30 minutes while the app is open and in
//     the foreground, in the same category and shape as the iPhone manual
//     reading in field_battery_prompt_controller.js (source: "auto" here)
// iPhone has no Battery API, so nothing here runs there and it never doubles
// up with the #4109 prompt.
//
// Monitoring is on unless the Tasker switched it off in Settings > Diagnostics.
// The toggle dispatches field:battery-monitoring-changed on window, which
// starts or stops everything straight away.
//
// Shift forms inside the layout log a snapshot with
// data-action="submit->field-battery#logShiftStart" / "#logShiftEnd".
//
// Dev shortcut (not a user feature): in the browser console run
//   localStorage.setItem("field:battery_reading_interval_ms", "10000")
// to log a reading after 10 seconds instead of 30 minutes. Remove the key to go back.
export default class extends Controller {
  get enabled() {
    return batteryMonitoringEnabled()
  }

  connect() {
    this._visibilityHandler = () => this._onVisibilityChange()
    document.addEventListener("visibilitychange", this._visibilityHandler)
    this._apply()
  }

  disconnect() {
    this._stopChecking()
    if (this._visibilityHandler) document.removeEventListener("visibilitychange", this._visibilityHandler)
  }

  // Called when the Settings toggle changes.
  monitoringChanged() {
    this._apply()
  }

  logShiftStart() {
    if (this.enabled) this._logSnapshot("Battery at shift start")
  }

  logShiftEnd() {
    if (this.enabled) this._logSnapshot("Battery at shift end")
    // Always, regardless of battery monitoring -- shift end is one of the two
    // forced "Foreground time" triggers (card #4838).
    logForegroundShiftEnd()
  }

  // -- private --

  _apply() {
    if (this.enabled) {
      initBatteryMonitor()
      this._startReadings()
    } else {
      stopBatteryMonitor()
      this._stopChecking()
    }
  }

  async _logSnapshot(message) {
    const snap = await getBatterySnapshot()
    if (!snap) return
    logEvent("battery", message, { level: snap.level, charging: snap.charging, ...foregroundTimeStamp() }).catch(() => {})
  }

  _startReadings() {
    if (typeof navigator.getBattery !== "function") return

    if (this._lastReadingAt() === null) this._storeLastReadingAt(Date.now())
    if (document.visibilityState === "visible") this._startChecking()
  }

  _onVisibilityChange() {
    if (!this.enabled) return

    if (document.visibilityState === "visible") {
      this._startReadings()
    } else {
      this._stopChecking()
    }
  }

  _startChecking() {
    this._stopChecking()
    this._check()
    this._timer = setInterval(() => this._check(), CHECK_EVERY_MS)
  }

  _stopChecking() {
    if (this._timer) clearInterval(this._timer)
    this._timer = null
  }

  async _check() {
    if (!this.enabled || document.visibilityState !== "visible") return
    if (Date.now() - this._lastReadingAt() < this._intervalMs()) return

    // Store first so overlapping checks while the reading resolves don't repeat it.
    this._storeLastReadingAt(Date.now())
    const snap = await getBatterySnapshot()
    if (!snap) return

    logEvent("battery", "Battery reading", {
      level: snap.level,
      charging: snap.charging,
      lowPowerMode: null,
      source: "auto",
      ...foregroundTimeStamp()
    }).catch(() => {})
  }

  _intervalMs() {
    const override = Number(this._read(BATTERY_READING_DEV_INTERVAL_KEY))
    return override > 0 ? override : BATTERY_READING_INTERVAL_MS
  }

  _lastReadingAt() {
    const stored = Number(this._read(BATTERY_READING_LAST_KEY))
    if (stored > 0) return stored
    return lastReadingAtFallback
  }

  _storeLastReadingAt(timestamp) {
    lastReadingAtFallback = timestamp
    try {
      localStorage.setItem(BATTERY_READING_LAST_KEY, String(timestamp))
    } catch {
      // storage blocked: the module value above still stops a repeat this session
    }
  }

  _read(key) {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }
}
